import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';
import { useAlert } from '../context/AlertContext';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import {
  ratingStatusColor,
  ratingStatusLabel,
} from '../services/ratingAnswerDisplay.service';
import {
  dispatchConfirmationText,
  groupResponsesByClient,
} from '../services/ratingDispatchOverview.service';
import { formatBackendDateTime } from '../services/backendTimestamp.service';
import {
  useGetOpenFormulareStatus,
  useGetRatingResponsesForLegalTransaction,
  useSendRating,
} from '../hooks/reactQuerys/RatingFormQueries';
import { RatingResponseDetailDialog } from './RatingResponseDetailDialog';

interface IProps {
  idLegalTransaction: number;
  /** Aktueller Status (100 = Abgeschlossen) — steuert, ob manuell versendet
   *  werden darf. */
  idLegalTransactionState?: number | null;
  closedAt?: string | null;
}

const STATE_ABGESCHLOSSEN = 100;

/**
 * Maske zum Durchführen der Bewertung eines Vorgangs: manueller Versand des
 * Bewertungsbogens an die Mandanten (OpenFormulare) und Übersicht der
 * bereits abgegebenen Antworten. Wird im Vorgangs-Tab „Bewertungsbögen"
 * angezeigt.
 */
export const RatingActionPanel: React.FC<IProps> = ({
  idLegalTransaction,
  idLegalTransactionState,
  closedAt,
}) => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const { responses, isLoadingResponses, refetchResponses } =
    useGetRatingResponsesForLegalTransaction(idLegalTransaction);
  const { sendRating, isPendingSend } = useSendRating();
  const { ofStatus } = useGetOpenFormulareStatus();

  const isClosed =
    idLegalTransactionState === STATE_ABGESCHLOSSEN ||
    (closedAt != null && closedAt !== '');
  const ofReady = ofStatus?.isReady ?? false;
  const [openDetail, setOpenDetail] = useState<number | null>(null);

  // Eine Zeile pro Mandant. Vorher stand je Antwort eine Zeile, wodurch
  // derselbe Mandant nach einem erneuten Versand zweimal erschien — ohne
  // Erklärung (QS #9, TF1).
  const clientGroups = useMemo(
    () => groupResponsesByClient(responses),
    [responses]
  );

  const handleSend = async () => {
    // Vor einem erneuten Versand sagen, was passiert: eine offene Einladung
    // wird wiederverwendet, eine bereits abgegebene führt zu einem neuen
    // Bogen. Ohne diese Rückfrage löste ein zweiter Klick unbemerkt einen
    // zweiten Versand aus (QS #9, TF1).
    const confirmation = dispatchConfirmationText(responses);
    if (confirmation) {
      const confirmed = await showConfirmDialogComponent(
        'Bewertungsbogen erneut versenden?',
        confirmation,
        'Erneut versenden',
        'Abbrechen',
        theme
      );
      if (!confirmed) return;
    }
    try {
      const result = (await sendRating(idLegalTransaction)) as {
        sent?: { idLegalClient: number; Email: string }[];
        skipped?: { idLegalClient: number; reason: string }[];
        form?: string;
        error?: string;
      };
      if (result.error) {
        showAlert({ text: result.error, severity: 'error' });
        return;
      }
      const count = result.sent?.length ?? 0;
      const skipped = result.skipped?.length ?? 0;
      showAlert({
        text:
          count === 0
            ? 'Es wurden keine Bewertungsbögen versendet.'
            : `${count} Bewertungsbogen-Mail(s) angestoßen${
                skipped ? `, ${skipped} übersprungen` : ''
              }.`,
        severity: count > 0 ? 'success' : 'warning',
      });
      await refetchResponses();
    } catch (err) {
      console.error(err);
      showAlert({ text: 'Versand fehlgeschlagen.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 1 }}
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <StarIcon color='primary' fontSize='small' />
          <Typography variant='subtitle1'>Versand &amp; Ergebnisse</Typography>
        </Stack>
        {isClosed && ofReady && (
          <Button
            variant='contained'
            startIcon={
              isPendingSend ? <CircularProgress size={16} /> : <SendIcon />
            }
            disabled={isPendingSend}
            onClick={handleSend}
          >
            Bewertungsbogen an Mandanten senden
          </Button>
        )}
      </Stack>

      {!ofReady && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Der Versand ist nicht möglich: die Erweiterung „OpenFormulare" ist
          nicht aktiviert oder nicht vollständig konfiguriert (Einstellungen →
          Erweiterungen). Automatische Trigger werden erst nach Aktivierung
          ausgeführt.
        </Alert>
      )}

      {ofReady && !isClosed && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Ein manueller Versand ist erst nach Abschluss des Vorgangs möglich.
          Der konfigurierte automatische Versand erfolgt unabhängig davon.
        </Alert>
      )}

      {isLoadingResponses ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : clientGroups.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Es wurde noch kein Bewertungsbogen an die Mandanten versendet.
        </Typography>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Mandant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Versendet</TableCell>
              <TableCell>Abgegeben</TableCell>
              <TableCell align='right'>Bewertung</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientGroups.map(group => {
              const rows = [group.latest, ...group.older];
              return rows.map((r, index) => {
                const isLatest = index === 0;
                return (
                  <TableRow
                    key={r.idRatingResponse}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpenDetail(r.idRatingResponse)}
                  >
                    <TableCell>
                      {isLatest ? (
                        <Stack>
                          <Typography variant='body2'>{group.name}</Typography>
                          {group.email && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {group.email}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        // Ältere Einladung desselben Mandanten — eingerückt,
                        // damit die Zuordnung ohne Nachdenken erkennbar ist.
                        <Tooltip title='Frühere Einladung an denselben Mandanten. Sie bleibt erhalten; ein erneuter Versand legt jeweils einen neuen Bogen an.'>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ pl: 3 }}
                          >
                            ↳ frühere Einladung
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={ratingStatusLabel(r.Status)}
                        color={ratingStatusColor(r.Status)}
                        variant={
                          r.Status === 'submitted' ? 'filled' : 'outlined'
                        }
                      />
                    </TableCell>
                    <TableCell>{formatBackendDateTime(r.SentAt)}</TableCell>
                    <TableCell>
                      {formatBackendDateTime(r.SubmittedAt)}
                    </TableCell>
                    <TableCell align='right'>
                      {r.OverallScore != null ? (
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='flex-end'
                          spacing={0.5}
                        >
                          <Typography variant='body2'>
                            {Number(r.OverallScore).toFixed(2)}
                          </Typography>
                          <StarIcon fontSize='small' color='warning' />
                        </Stack>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                  </TableRow>
                );
              });
            })}
          </TableBody>
        </Table>
      )}

      {clientGroups.length > 0 && (
        // Ohne diesen Satz wartet man vor einer Tabelle, die sich scheinbar
        // nicht rührt: „Geöffnet" und „Abgegeben" stammen aus einem Abruf bei
        // OpenFormulare, der stündlich läuft (ProcessManager-Typ 32,
        // Cooldown 1 Stunde). Die QS hielt das für einen Fehler (#9, TF3).
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mt: 1 }}
        >
          „Geöffnet" und „Abgegeben" werden einmal pro Stunde von OpenFormulare
          abgeholt. Ein gerade ausgefüllter Bogen erscheint hier daher erst beim
          nächsten Abruf.
        </Typography>
      )}

      <RatingResponseDetailDialog
        idRatingResponse={openDetail}
        onClose={() => setOpenDetail(null)}
      />
    </Box>
  );
};
